import { useEffect, useRef, useState } from 'react';
import type { WidgetPoint } from '../api';

/**
 * Oficialni widget PPL pro vyber vydejniho mista.
 *
 * Nacita se primo z ppl.cz a bezi u zakaznika v prohlizeci, takze
 * nepotrebuje zadny nas import ani pristup k CPL API. Widget v2 chce
 * `api-key`, coz je neco jineho nez OAuth udaje k CPL – je to klic pro
 * prohlizec, ne tajemstvi. Bez klice se pouzije starsi widget v1.
 *
 * Zdroj: PPL vlastni WooCommerce plugin (github.com/ppl-cz/PPL-WooCommerce),
 * src/Front/Components/Map.
 */

const WIDGET_CSS = 'https://www.ppl.cz/sources/map/main.css';
const WIDGET_JS = 'https://www.ppl.cz/sources/map/main.js';

interface Props {
  readonly apiKey: string;
  readonly selected: WidgetPoint | null;
  readonly onSelect: (point: WidgetPoint) => void;
  /** Kdyz zakaznik plati dobirkou, misto ji musi umet prijmout. */
  readonly requireCashOnDelivery?: boolean;
}

/**
 * Prevod udalosti widgetu na nas tvar.
 *
 * Widget v1 a v2 vraci ruzne struktury a i uvnitr jedne verze se lisi nazvy
 * (`zipCode` vs `zip`, `gps` vs ploche `latitude`). Prevod je zamerne
 * samostatna funkce, aby sel otestovat bez nacitani ciziho skriptu.
 */
export function mapWidgetPoint(detail: unknown): WidgetPoint | null {
  if (typeof detail !== 'object' || detail === null) return null;
  const raw = detail as Record<string, unknown>;

  const address = (raw['address'] ?? raw) as Record<string, unknown>;
  const gps = (address['gps'] ?? raw['gps'] ?? {}) as Record<string, unknown>;
  const payments = (raw['paymentMethods'] ?? {}) as Record<string, unknown>;

  const code = str(raw['code'] ?? raw['id'] ?? raw['externalId']);
  const name = str(raw['name']);
  if (code === '' || name === '') return null;

  return {
    code,
    name,
    street: str(address['street'] ?? raw['street']),
    city: str(address['city'] ?? raw['city']),
    postalCode: str(address['zipCode'] ?? address['zip'] ?? raw['zipCode'] ?? raw['zip']),
    countryCode: str(address['countryCode'] ?? address['country'] ?? raw['country']) || 'CZ',
    latitude: num(gps['lat'] ?? gps['latitude'] ?? raw['latitude']),
    longitude: num(gps['lon'] ?? gps['lng'] ?? gps['longitude'] ?? raw['longitude']),
    accessPointType: normalizeAccessPointType(raw['accessPointType'] ?? raw['type']),
    cardPayment: bool(payments['card'] ?? raw['activeCardPayment']),
    cashPayment: bool(payments['cash'] ?? raw['activeCashPayment']),
  };
}

export function PplWidgetPicker({ apiKey, selected, onSelect, requireCashOnDelivery }: Props) {
  const hostRef = useRef<HTMLDivElement>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'failed'>('loading');

  useEffect(() => {
    const host = hostRef.current;
    if (host === null) return;

    host.replaceChildren();

    // v2 je webova komponenta s klicem, v1 obycejny div s data atributy.
    const element =
      apiKey === ''
        ? createLegacyElement(requireCashOnDelivery === true)
        : createWidgetElement(apiKey, requireCashOnDelivery === true);
    host.appendChild(element);

    if (document.getElementById('ppl-widget-css') === null) {
      const css = document.createElement('link');
      css.id = 'ppl-widget-css';
      css.rel = 'stylesheet';
      css.href = WIDGET_CSS;
      document.head.appendChild(css);
    }

    // Vyber muze prijit tremi cestami podle verze widgetu – posloucháme vsechny.
    const handle = (detail: unknown) => {
      const point = mapWidgetPoint(detail);
      if (point !== null) onSelect(point);
    };

    const onElementSelect = (event: Event) => handle((event as CustomEvent).detail);
    const onDocumentSelect = (event: Event) => handle((event as CustomEvent).detail);
    const onMessage = (event: MessageEvent) => {
      // Zpravy z ciziho okna neparsujeme naslepo.
      if (typeof event.data !== 'string' && typeof event.data !== 'object') return;
      try {
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        handle(data?.parcelshop ?? data?.point ?? data);
      } catch {
        // Cizi postMessage, ktery nas nezajima.
      }
    };

    element.addEventListener('ppl-accesspointwidget-select', onElementSelect);
    document.addEventListener('ppl-parcelshop-map', onDocumentSelect);
    window.addEventListener('message', onMessage);

    const existing = document.getElementById('ppl-widget-js');
    if (existing === null) {
      const script = document.createElement('script');
      script.id = 'ppl-widget-js';
      script.src = WIDGET_JS;
      script.async = true;
      script.onload = () => setStatus('ready');
      script.onerror = () => setStatus('failed');
      document.body.appendChild(script);
    } else {
      setStatus('ready');
    }

    return () => {
      element.removeEventListener('ppl-accesspointwidget-select', onElementSelect);
      document.removeEventListener('ppl-parcelshop-map', onDocumentSelect);
      window.removeEventListener('message', onMessage);
    };
  }, [apiKey, onSelect, requireCashOnDelivery]);

  return (
    <div className="ppl-widget">
      {status === 'failed' && (
        <p className="alert">
          Mapu výdejních míst se nepodařilo načíst. Zkus to prosím znovu, nebo zvol
          doručení na adresu.
        </p>
      )}
      {status === 'loading' && <p className="muted small">Načítám mapu výdejních míst…</p>}

      <div ref={hostRef} className="ppl-widget-host" />

      {selected !== null && (
        <p className="picked" role="status">
          <strong>{selected.name}</strong>
          <span className="muted small">
            {selected.street}, {selected.postalCode} {selected.city}
          </span>
        </p>
      )}
    </div>
  );
}

/** Widget v2 – webova komponenta s konfiguraci v JSON atributu. */
function createWidgetElement(apiKey: string, codRequired: boolean): HTMLElement {
  const element = document.createElement('ppl-access-point-widget');
  element.id = 'pplWidget';
  element.setAttribute('api-key', apiKey);
  element.setAttribute(
    'config',
    JSON.stringify({
      mode: 'SHOPCART',
      viewMode: 'inline',
      defaultCountry: 'CZ',
      allowedCountries: ['CZ'],
      defaultLanguage: 'cs',
      allowedAccessPointTypes: ['ParcelShop', 'ParcelBox', 'AlzaBox'],
      ...(codRequired ? { codRequired: true } : {}),
    }),
  );
  return element;
}

/** Widget v1 – div s pevnym id, ktere skript hleda. */
function createLegacyElement(codRequired: boolean): HTMLElement {
  const element = document.createElement('div');
  element.id = 'ppl-parcelshop-map';
  element.setAttribute('data-country', 'cz');
  element.setAttribute('data-countries', 'cz');
  element.setAttribute('data-language', 'cs');
  element.setAttribute('data-mode', 'default');
  if (codRequired) element.setAttribute('data-initialfilters', 'ParcelShop');
  return element;
}

/**
 * Widget hlasi typ mista jako PPL_SHOP nebo ALZA_BOX, ale CPL API pri
 * zakladani zasilky ceka ParcelShop / AlzaBox. Prevod dela i vlastni
 * plugin PPL – bez nej by zasilka spadla az pri predani.
 */
const ACCESS_POINT_TYPE: Readonly<Record<string, string>> = {
  PPL_SHOP: 'ParcelShop',
  PARCEL_SHOP: 'ParcelShop',
  ALZA_BOX: 'AlzaBox',
  PARCEL_BOX: 'ParcelBox',
};

function normalizeAccessPointType(value: unknown): string | null {
  const raw = str(value);
  if (raw === '') return null;
  return ACCESS_POINT_TYPE[raw.toUpperCase()] ?? raw;
}

function str(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function num(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function bool(value: unknown): boolean | null {
  return typeof value === 'boolean' ? value : null;
}
